import { useModuleAccess } from "@/hooks/useModuleAccess";
import { AppLayout } from "@/components/AppLayout";
import { ProfileHeader } from "@/components/ProfileHeader";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiGetEmployeeByEmail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfileAboutTab } from "@/components/profile/ProfileAboutTab";
import { ProfilePersonalInfoTab } from "@/components/profile/ProfilePersonalInfoTab";
import { ProfileSalaryTab } from "@/components/profile/ProfileSalaryTab";
import { ProfileNotesTab } from "@/components/profile/ProfileNotesTab";
import { ProfileAuditTab } from "@/components/profile/ProfileAuditTab";
import { ProfileExitTab } from "@/components/profile/ProfileExitTab";
import {
  User, FileText, DollarSign, MessageSquare, History, LogOut
} from "lucide-react";
import { ProfileHeaderSkeleton, CardGridSkeleton } from "@/components/PageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";

export default function MyProfile() {
  const { t } = useTranslation();
  const { canAccess } = useModuleAccess();
  const { user } = useAuth();
  const isAdmin = user?.role.mainRole === "admin";

  const { data: employee, isLoading } = useQuery({
    queryKey: ["employee-by-email", user?.email],
    queryFn: () => apiGetEmployeeByEmail(user!.email),
    enabled: !!user?.email,
  });

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-6 w-full">
          <ProfileHeaderSkeleton />
          <div className="flex gap-2 border-b border-border/60 pb-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-24" />
            ))}
          </div>
          <CardGridSkeleton count={6} />
        </div>
      </AppLayout>
    );
  }

  if (!employee) {
    return (
      <AppLayout>
        <div className="text-center py-20 text-muted-foreground">
          {t("profile.profileNotFound")}
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        <ProfileHeader employee={employee} isOwnProfile />

        <Tabs defaultValue="about" className="w-full">
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger
              value="about"
              className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <User className="h-3.5 w-3.5" />
              {t("profile.tabs.about")}
            </TabsTrigger>
            <TabsTrigger
              value="personal"
              className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <FileText className="h-3.5 w-3.5" />
              {t("profile.tabs.personalInfo")}
            </TabsTrigger>
            {canAccess("salary") && <TabsTrigger
              value="salary"
              className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <DollarSign className="h-3.5 w-3.5" />
              {t("profile.tabs.salary")}
            </TabsTrigger>}
            <TabsTrigger
              value="notes"
              className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              {t("profile.tabs.notes")}
            </TabsTrigger>
            <TabsTrigger
              value="exit"
              className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <LogOut className="h-3.5 w-3.5" />
              {t("offboardingModule.exit")}
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger
                value="audit"
                className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
              >
                <History className="h-3.5 w-3.5" />
                {t("profile.tabs.auditLog")}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="about" className="mt-4">
            <ProfileAboutTab employee={employee} />
          </TabsContent>

          <TabsContent value="personal" className="mt-4">
            <ProfilePersonalInfoTab employee={employee} isOwnProfile />
          </TabsContent>

          {canAccess("salary") && <TabsContent value="salary" className="mt-4">
            <ProfileSalaryTab employee={employee} isOwnProfile />
          </TabsContent>}

          <TabsContent value="notes" className="mt-4">
            <ProfileNotesTab employee={employee} />
          </TabsContent>

          <TabsContent value="exit" className="mt-4">
            <ProfileExitTab employee={employee} isOwnProfile />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="audit" className="mt-4">
              <ProfileAuditTab employee={employee} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </AppLayout>
  );
}
